import type { MutableRefObject } from 'react';

import type { DocNode } from '@atlaskit/adf-schema';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { NextEditorPlugin } from '@atlaskit/editor-common/types';
import type { Node as PMNode, Schema } from '@atlaskit/editor-prosemirror/model';
import type { EditorView, NodeView } from '@atlaskit/editor-prosemirror/view';

import {
	CONNECTOR_SOURCE_CHIP_NODE_NAME,
	connectorSourceChipNodeSpec,
} from './connector-source-chip-node';
import { createConnectorSourceChipNodeView } from './create-chip-node-view';

export type ConnectorSourceEntry = {
	key: string;
	display: string;
	source?: string;
	iconUrl?: string;
};

export type ConnectorSourceInputRuleConfig = {
	knownSourcesRef: MutableRefObject<ConnectorSourceEntry[]>;
	enableIconChip?: boolean;
};

const hasObjectShape = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

const getConnectorSourceDisplay = (value: unknown): string => {
	if (!hasObjectShape(value) || !hasObjectShape(value.attrs)) {
		return '';
	}

	return typeof value.attrs.display === 'string' ? value.attrs.display : '';
};

const normalizeConnectorSourceJson = (_key: string, value: unknown): unknown => {
	if (hasObjectShape(value) && value.type === CONNECTOR_SOURCE_CHIP_NODE_NAME) {
		return {
			type: 'text',
			text: getConnectorSourceDisplay(value),
		};
	}

	return value;
};

export const normalizeConnectorSourceDoc = (doc: DocNode): DocNode =>
	JSON.parse(JSON.stringify(doc, normalizeConnectorSourceJson));

/**
 * Given the text before the cursor, try to find a connector source name
 * at the end. Matching is case-insensitive against `entry.key`.
 * Returns the matched entry and the start offset, or null.
 */
export const findTrailingSource = (
	textBefore: string,
	sources: ConnectorSourceEntry[],
): { entry: ConnectorSourceEntry; name: string; startOffset: number } | null => {
	const lower = textBefore.toLowerCase();
	for (const entry of sources) {
		const { key, display } = entry;
		if (lower.endsWith(key)) {
			const startOffset = textBefore.length - key.length;
			if (startOffset === 0 || /\s/.test(textBefore[startOffset - 1])) {
				return { entry, name: display, startOffset };
			}
		}
	}
	return null;
};

type DispatchableEditorView = Pick<EditorView, 'state' | 'dispatch'>;

export const deleteAdjacentConnectorSourceChip = (
	view: DispatchableEditorView,
	event: KeyboardEvent,
): boolean => {
	if (event.key !== 'Backspace') {
		return false;
	}

	const { state } = view;
	const { selection } = state;

	if (!selection.empty) {
		return false;
	}

	const { $from } = selection;
	const nodeBefore = $from.nodeBefore;

	if (nodeBefore?.type.name === CONNECTOR_SOURCE_CHIP_NODE_NAME) {
		const chipEndPosition = $from.pos;
		const chipStartPosition = chipEndPosition - nodeBefore.nodeSize;

		event.preventDefault();
		view.dispatch(state.tr.delete(chipStartPosition, chipEndPosition));
		return true;
	}

	if (nodeBefore?.isText && nodeBefore.text === ' ') {
		const positionBeforeSpace = $from.pos - 1;
		const resolvedPositionBeforeSpace = state.doc.resolve(positionBeforeSpace);
		const nodeBeforeSpace = resolvedPositionBeforeSpace.nodeBefore;

		if (nodeBeforeSpace?.type.name === CONNECTOR_SOURCE_CHIP_NODE_NAME) {
			const chipStartPosition = positionBeforeSpace - nodeBeforeSpace.nodeSize;

			event.preventDefault();
			view.dispatch(state.tr.delete(chipStartPosition, $from.pos));
			return true;
		}
	}

	return false;
};

const insertLegacyCodeChip = ({
	schema,
	match,
	absoluteStart,
	absoluteEnd,
	view,
}: {
	schema: Schema;
	match: { name: string };
	absoluteStart: number;
	absoluteEnd: number;
	view: DispatchableEditorView;
}): boolean => {
	const codeMark = schema.marks.code;
	if (!codeMark) {
		return false;
	}

	const codeTextNode = schema.text(match.name, [codeMark.create()]);
	const tr = view.state.tr.replaceWith(absoluteStart, absoluteEnd, [
		codeTextNode,
		schema.text(' '),
	]);

	view.dispatch(tr);
	return true;
};

export const handleConnectorSourceTextInput = ({
	view,
	schema,
	from,
	text,
	sourceNames,
	enableIconChip,
}: {
	view: DispatchableEditorView;
	schema: Schema;
	from: number;
	text: string;
	sourceNames: ConnectorSourceEntry[];
	enableIconChip: boolean;
}): boolean => {
	if (text !== ' ') {
		return false;
	}

	if (!sourceNames.length) {
		return false;
	}

	const { state } = view;
	const { $from } = state.selection;
	const textBefore = $from.parent.textBetween(0, $from.parentOffset, undefined, '\ufffc') + '';

	const match = findTrailingSource(textBefore, sourceNames);
	if (!match) {
		return false;
	}

	const absoluteStart = $from.start() + match.startOffset;
	const absoluteEnd = from;

	if (!enableIconChip) {
		return insertLegacyCodeChip({
			schema,
			match,
			absoluteStart,
			absoluteEnd,
			view,
		});
	}

	const connectorSourceChipNode = schema.nodes[CONNECTOR_SOURCE_CHIP_NODE_NAME];
	if (!connectorSourceChipNode) {
		return insertLegacyCodeChip({
			schema,
			match,
			absoluteStart,
			absoluteEnd,
			view,
		});
	}

	const chipNode = connectorSourceChipNode.create({
		source: match.entry.source ?? match.entry.key,
		display: match.entry.display,
		iconUrl: match.entry.iconUrl ?? null,
	});

	const tr = state.tr.replaceWith(absoluteStart, absoluteEnd, [chipNode, schema.text(' ')]);

	view.dispatch(tr);
	return true;
};

export const connectorSourceInputRulePlugin: NextEditorPlugin<
	'connectorSourceInputRule',
	{
		pluginConfiguration: ConnectorSourceInputRuleConfig;
	}
> = ({ config: { knownSourcesRef, enableIconChip = false } }) => {
	return {
		name: 'connectorSourceInputRule',
		...(enableIconChip
			? {
					nodes() {
						return [{ name: CONNECTOR_SOURCE_CHIP_NODE_NAME, node: connectorSourceChipNodeSpec }];
					},
				}
			: {}),
		pmPlugins() {
			return [
				{
					name: 'connectorSourceInputRule',
					plugin: ({ schema }) => {
						return new SafePlugin({
							props: {
								handleKeyDown(view: EditorView, event: KeyboardEvent): boolean {
									if (!enableIconChip) {
										return false;
									}

									return deleteAdjacentConnectorSourceChip(view, event);
								},
								...(enableIconChip
									? {
											nodeViews: {
												[CONNECTOR_SOURCE_CHIP_NODE_NAME]: (pmNode: PMNode): NodeView =>
													createConnectorSourceChipNodeView(pmNode),
											},
										}
									: {}),
								handleTextInput(
									view: EditorView,
									from: number,
									_to: number,
									text: string,
								): boolean {
									return handleConnectorSourceTextInput({
										view,
										schema,
										from,
										text,
										sourceNames: knownSourcesRef.current,
										enableIconChip,
									});
								},
							},
						});
					},
				},
			];
		},
	};
};
